import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { get, post, upload } from "../../utils/request";
import { getFilePreviewUrl } from "../../utils/attachmentPreview";
import { useToast } from "../../Components/Toast/Toast";
import { ReactComponent as Cross } from "../../Resources/image/cross.svg";
import AnimatedCart from "../AnimatedPage/AnimatedCart";
import styles from "./Profile.module.css";
import { t, noAutoTranslateUserTextProps } from "../../utils/i18n";

// 国家选项：value 为接口使用的国家代码
const COUNTRY_OPTIONS = [
  { code: "", name: "Select country" },
  { code: "CN", name: "China" },
  { code: "RU", name: "Russia" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "JP", name: "Japan" },
  { code: "KR", name: "Korea" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IN", name: "India" },
  { code: "BR", name: "Brazil" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "SG", name: "Singapore" },
  { code: "MY", name: "Malaysia" },
  { code: "TH", name: "Thailand" },
  { code: "VN", name: "Vietnam" },
  { code: "ID", name: "Indonesia" },
  { code: "PH", name: "Philippines" },
  { code: "TW", name: "Taiwan" },
  { code: "HK", name: "Hong Kong" },
  { code: "MO", name: "Macau" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "PL", name: "Poland" },
  { code: "TR", name: "Turkey" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "OTHER", name: "Other" },
];

const Profile = props => {
  const { isLoggedIn, onClose } = props;
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [form, setForm] = useState({
    avatarUrl: "",
    avatarAttachmentUuid: "",
    nickname: "",
    countryCode: "",
    introduction: "",
  });

  useEffect(() => {
    if (isLoggedIn) loadUserInfo();
  }, [isLoggedIn]);

  /** 从接口响应中取出用户对象（兼容 res.data 与 res.data.data） */
  const unwrapUser = res => {
    const raw = res?.data ?? res;
    if (!raw || typeof raw !== "object") return raw;
    const inner = raw?.data;
    if (inner && typeof inner === "object" && (inner.nickname !== undefined || inner.avatarUrl !== undefined || inner.countryCode !== undefined || inner.id !== undefined)) {
      return inner;
    }
    return raw;
  };

  /** 将接口返回的用户对象解析为表单状态（含头像 id → 预览链接） */
  const parseUserToForm = async data => {
    const avatarId =
      data?.attachmentUuid ??
      data?.avatarAttachmentUuid ??
      data?.avatarUrl ??
      "";
    const isLikelyId = avatarId && typeof avatarId === "string" && !avatarId.startsWith("http");
    const avatarUrl = isLikelyId
      ? await getFilePreviewUrl(avatarId)
      : avatarId || "";
    return {
      avatarUrl,
      avatarAttachmentUuid: isLikelyId ? avatarId : "",
      nickname: data?.nickname ?? "",
      countryCode: data?.countryCode ?? "",
      introduction: data?.introduction ?? "",
    };
  };

  const loadUserInfo = async () => {
    setLoading(true);
    try {
      const res = await get("/user/getCurrentUserInfo");
      const data = unwrapUser(res);
      setForm(await parseUserToForm(data || {}));
    } catch (err) {
      toast.error(err?.message || t("profile.error.fetch", "Failed to fetch user profile"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAvatarClick = () => {
    if (avatarUploading) return;
    fileInputRef.current?.click();
  };

  const handleAvatarFileChange = async e => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      toast.warning(t("profile.warn.selectImage", "Please select an image file"));
      e.target.value = "";
      return;
    }
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await upload("/user/uploadAvatar", formData);
      // 接口返回 { data: "附件id" }，通过公用预览接口获取图片链接
      const rawData = res?.data ?? res;
      const attachmentId =
        typeof rawData === "string"
          ? rawData
          : rawData?.url ?? rawData?.avatarUrl ?? rawData?.id ?? rawData?.data ?? "";
      const url = attachmentId
        ? await getFilePreviewUrl(attachmentId)
        : "";
      if (url) {
        setForm(prev => ({
          ...prev,
          avatarUrl: url,
          avatarAttachmentUuid: attachmentId,
        }));
        toast.success(t("profile.success.avatarUpdated", "Avatar updated"));
      } else {
        toast.error(
          attachmentId
            ? t("profile.error.avatarLink", "Failed to get avatar link")
            : t(
                "profile.error.avatarMissingId",
                "Upload successful but no attachment ID returned",
              ),
        );
      }
    } catch (err) {
      toast.error(err?.message || t("profile.error.avatarUpload", "Failed to upload avatar"));
    } finally {
      setAvatarUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      // avatarUrl 字段必传，值为附件 ID（attachmentUuid）；未设置头像时传 null
      const res = await post("/user/updateUserInfo", {
        avatarUrl: form.avatarAttachmentUuid || null,
        nickname: form.nickname,
        countryCode: form.countryCode,
        introduction: form.introduction,
      });
      // 修改接口直接返回用户信息，用返回的 data 更新表单反显
      const data = unwrapUser(res);
      if (data && typeof data === "object") {
        setForm(await parseUserToForm(data));
      }
      toast.success(t("profile.success.save", "Save successful"));
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("profile.error.save", "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const panelContent = (
    <div className={styles.profileWindow}>
      <div className={styles.back} onClick={onClose} aria-hidden="true" />
      <AnimatedCart>
        <div className={styles.panel}>
          <div className={styles.top}>
            <div className={styles.topHeader}>
              <div>
                <h2 className={styles.title}>{t("profile.title", "Edit profile")}</h2>
                <p className={styles.subtitle}>
                  {t("profile.subtitle", "Update your information")}
                </p>
              </div>
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label={t("aria.close", "Close")}
              >
                <Cross className={styles.cross} />
              </button>
            </div>

            {loading ? (
              <div className={styles.loading}>{t("profile.loading", "loading...")}</div>
            ) : (
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.avatarSection}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className={styles.fileInput}
                    onChange={handleAvatarFileChange}
                    aria-hidden="true"
                  />
                  <div
                    className={styles.avatarWrap}
                    onClick={handleAvatarClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === "Enter" && handleAvatarClick()}
                    aria-label={t("profile.aria.uploadAvatar", "Upload avatar")}
                  >
                    <div className={styles.avatarBox}>
                      {avatarUploading ? (
                        <div className={styles.avatarPlaceholder}>
                          <span className={styles.avatarLoading}>
                            {t("profile.avatar.uploading", "Uploading...")}
                          </span>
                        </div>
                      ) : form.avatarUrl ? (
                        <>
                          <img
                            src={form.avatarUrl}
                            alt=""
                            className={styles.avatarImg}
                          />
                          <span className={styles.avatarOverlay}>
                            {t("profile.avatar.change", "Change avatar")}
                          </span>
                        </>
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          <span className={styles.avatarHint}>
                            {t("profile.avatar.clickUpload", "Click to upload")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>
                    {t("profile.label.nickname", "Nickname")}
                  </label>
                  <input
                    type="text"
                    className={[styles.input, "notranslate"].join(" ")}
                    placeholder={t("profile.label.nickname", "Nickname")}
                    value={form.nickname}
                    onChange={e => handleChange("nickname", e.target.value)}
                    {...noAutoTranslateUserTextProps}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>{t("profile.label.country", "Country")}</label>
                  <select
                    className={styles.countrySelect}
                    value={form.countryCode}
                    onChange={e =>
                      handleChange("countryCode", e.target.value)
                    }
                    aria-label={t("profile.label.country", "Country")}
                  >
                    {COUNTRY_OPTIONS.map(opt => (
                      <option key={opt.code || "empty"} value={opt.code}>
                        {opt.code
                          ? opt.name
                          : t("profile.country.select", "Select country")}
                      </option>
                    ))}
                    {form.countryCode &&
                      !COUNTRY_OPTIONS.some(o => o.code === form.countryCode) && (
                        <option value={form.countryCode}>
                          {form.countryCode}
                        </option>
                      )}
                  </select>
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>
                    {t("profile.label.introduction", "Introduction")}
                  </label>
                  <textarea
                    className={styles.textarea}
                    placeholder={t("profile.label.introduction", "Introduction")}
                    rows={4}
                    value={form.introduction}
                    onChange={e =>
                      handleChange("introduction", e.target.value)
                    }
                  />
                </div>

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={onClose}
                  >
                    {t("profile.button.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={saving}
                  >
                    {saving
                      ? t("profile.button.saving", "Saving...")
                      : t("profile.button.save", "Save")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </AnimatedCart>
    </div>
  );

  if (!isLoggedIn) return null;
  return ReactDOM.createPortal(panelContent, document.body);
};

export default Profile;
